'use client';

import { Icon, WinnerTicket } from '@/components';
import { cn } from '@/libs';
import type { HighlightedRaffleDetails } from '@/types/models/HighlightedRaffleDetails';
import { formatCurrency } from '@/utils/formatters';

type MainPrize = NonNullable<HighlightedRaffleDetails['mainPrizes']>[0];

type PrizeCardProps = {
  prize: MainPrize;
  index: number;
  totalPrizes?: number;
  className?: string;
};

export const PrizeCard = ({ prize, className }: PrizeCardProps) => {
  const prizeNumber = (prize.index ?? 0) + 1;
  const formattedValue = prize.prizeValue
    ? formatCurrency(prize.prizeValue)
    : 'Valor não informado';

  const hasWinner = prize.userName || prize.userCpf || prize.userPhone;

  return (
    <div className={cn('flex flex-col space-y-2', className)}>
      <div className="flex items-center space-x-2">
        <h4 className="font-bold text-xl text-foreground">{prizeNumber}º Giro</h4>
      </div>

      <div className="space-y-1">
        <p className="text-foreground/80 text-sm">
          {prize.name || `Giro ${prizeNumber}`}
        </p>
        <p className="font-medium text-primary">{formattedValue}</p>
      </div>
      {hasWinner && (
        <div className="w-full md:h-32 mt-3">
          <p className="font-medium text-base">Ganhador(a):</p>
          <div className="flex flex-col justify-between p-2 bg-black/20 w-full h-16 rounded-xl">
            <div className="text-sm md:text-[16px] font-bold text-center text-foreground/50">
              {prize.userName?.toUpperCase() || 'GANHADOR'}
            </div>
            <div className="flex items-center justify-between w-full mt-0.5 md:px-2">
              {prize.userCpf && (
                <div className="flex items-center gap-1">
                  <Icon.UserAccount width={14} height={11} className="fill-white/50" />
                  <span className="text-xs text-foreground/50">{prize.userCpf}</span>
                </div>
              )}
              {prize.userPhone && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-foreground/50">{prize.userPhone}</span>
                </div>
              )}
            </div>
          </div>

          {prize.winningSequence && prize.winningSequence.length > 0 && (
            <>
              <div className="h-0.5 border border-foreground/5 border-dashed mx-2.5"></div>
              <div className="flex justify-between p-2 bg-black/20 w-full h-14 md:h-16 rounded-xl">
                <div className="flex flex-col items-center w-1/2">
                  <span className="text-sm uppercase text-foreground/50 font-bold">
                    Ganhou:
                  </span>
                  <span className="font-bold text-sm text-foreground/50">
                    {formattedValue}
                  </span>
                </div>
                <div className="flex items-center w-1/2">
                  <WinnerTicket
                    value={prize.winningSequence.join('')}
                    className="w-full h-8"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
